import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthProvider';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { getPlatformMetrics } from '../../services/platformAdminService';

const {
  FiHome,
  FiUsers,
  FiFileText,
  FiActivity,
  FiShield,
  FiAlertTriangle,
  FiCheckCircle,
  FiTrendingUp,
  FiRefreshCw,
  FiBriefcase
} = FiIcons;

/**
 * PlatformDashboard - Main dashboard for platform admins
 * Shows overview of platform status and quick actions with live metrics
 */
const PlatformDashboard = () => {
  const { user, superadminRole, hasSuperadminPermission } = useAuth();
  
  // Metrics state
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Fetch metrics on mount
  useEffect(() => {
    if (hasSuperadminPermission('metrics:read')) {
      fetchMetrics();
    }
  }, [hasSuperadminPermission]);

  const fetchMetrics = async () => {
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const data = await getPlatformMetrics();
      setMetrics(data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setMetricsError(error.message || 'Failed to load metrics');
    } finally {
      setMetricsLoading(false);
    }
  };

  // Role display names
  const roleDisplayNames = {
    platform_owner: 'Platform Owner',
    platform_admin: 'Platform Admin',
    subscription_admin: 'Subscription Admin',
    support_admin: 'Support Admin'
  };

  // Role descriptions
  const roleDescriptions = {
    platform_owner: 'Full access to all platform operations including managing other superadmins.',
    platform_admin: 'Manage tenants, users, and view all platform metrics.',
    subscription_admin: 'Manage subscriptions and billing, view tenant information.',
    support_admin: 'Read-only access to tenants, users, and metrics for support purposes.'
  };

  // Quick action cards based on permissions
  const quickActions = [
    {
      title: 'View Metrics',
      description: 'Platform-wide statistics and usage data',
      icon: FiActivity,
      path: '/platform-admin/metrics',
      permission: 'metrics:read',
      color: 'blue'
    },
    {
      title: 'Manage Tenants',
      description: 'View and manage organization accounts',
      icon: FiHome,
      path: '/platform-admin/tenants',
      permission: 'tenants:read',
      color: 'green'
    },
    {
      title: 'Manage Users',
      description: 'View and manage tenant users',
      icon: FiUsers,
      path: '/platform-admin/users',
      permission: 'users:read',
      color: 'purple'
    },
    {
      title: 'Audit Log',
      description: 'Review all superadmin actions',
      icon: FiFileText,
      path: '/platform-admin/audit',
      permission: 'audit:read',
      color: 'orange'
    }
  ];

  const visibleActions = quickActions.filter(action => 
    hasSuperadminPermission(action.permission)
  );

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    green: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
  };

  const iconColorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600'
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Welcome to Platform Admin
            </h1>
            <p className="text-indigo-100 mb-4">
              {user?.email}
            </p>
            <div className="flex items-center space-x-2 bg-white/20 rounded-lg px-3 py-2 w-fit">
              <SafeIcon icon={FiShield} className="w-4 h-4" />
              <span className="text-sm font-medium">
                {roleDisplayNames[superadminRole] || superadminRole}
              </span>
            </div>
          </div>
          <div className="hidden md:block">
            <SafeIcon icon={FiShield} className="w-16 h-16 text-white/30" />
          </div>
        </div>
      </div>

      {/* Role Description */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Your Access Level</h3>
            <p className="text-sm text-gray-600 mt-1">
              {roleDescriptions[superadminRole] || 'You have superadmin access to this platform.'}
            </p>
          </div>
        </div>
      </div>

      {/* MFA Reminder (placeholder - will be implemented) */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-medium text-amber-900">MFA Setup Required</h3>
            <p className="text-sm text-amber-700 mt-1">
              Multi-factor authentication is mandatory for all superadmin accounts. 
              Please set up MFA to perform administrative actions.
            </p>
            <button className="mt-2 text-sm font-medium text-amber-700 hover:text-amber-800 underline">
              Set up MFA →
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {visibleActions.map((action) => (
            <a
              key={action.path}
              href={`#${action.path}`}
              className={`block p-4 rounded-lg border transition-colors ${colorClasses[action.color]}`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <SafeIcon icon={action.icon} className={`w-6 h-6 ${iconColorClasses[action.color]}`} />
                <h3 className="font-medium">{action.title}</h3>
              </div>
              <p className="text-sm opacity-80">{action.description}</p>
            </a>
          ))}
        </div>
      </div>

      {/* Platform Overview - Live Metrics */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Platform Overview</h2>
          <div className="flex items-center space-x-3">
            {lastRefresh && (
              <span className="text-xs text-gray-500">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={fetchMetrics}
              disabled={metricsLoading}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${metricsLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {metricsError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-700">{metricsError}</p>
          </div>
        )}

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Tenants</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metricsLoading ? '—' : metrics?.totals?.totalTenants || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <SafeIcon icon={FiHome} className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Active organizations</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metricsLoading ? '—' : metrics?.totals?.totalUsers || 0}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Across all tenants</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Cases</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metricsLoading ? '—' : metrics?.totals?.totalCases || 0}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <SafeIcon icon={FiFileText} className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {metricsLoading ? '—' : metrics?.totals?.activeCases || 0} active
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Staff</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metricsLoading ? '—' : metrics?.totals?.totalStaff || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <SafeIcon icon={FiBriefcase} className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Employee records</p>
          </div>
        </div>

        {/* Activity Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Cases (Last 7 Days)</span>
            </div>
            <p className="text-3xl font-bold text-blue-700">
              {metricsLoading ? '—' : metrics?.totals?.casesLast7Days || 0}
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-900">Cases (Last 30 Days)</span>
            </div>
            <p className="text-3xl font-bold text-green-700">
              {metricsLoading ? '—' : metrics?.totals?.casesLast30Days || 0}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-lg border border-purple-200 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiActivity} className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">Total Workflows</span>
            </div>
            <p className="text-3xl font-bold text-purple-700">
              {metricsLoading ? '—' : metrics?.totals?.totalWorkflows || 0}
            </p>
          </div>
        </div>

        {/* Tenant Breakdown Table */}
        {metrics?.tenants && metrics.tenants.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-medium text-gray-900">Tenant Breakdown</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Tenant</th>
                    <th className="px-4 py-3 text-center">Users</th>
                    <th className="px-4 py-3 text-center">Cases</th>
                    <th className="px-4 py-3 text-center">Active</th>
                    <th className="px-4 py-3 text-center">Staff</th>
                    <th className="px-4 py-3 text-center">Workflows</th>
                    <th className="px-4 py-3 text-left">Last Activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {metrics.tenants.map((tenant) => (
                    <tr key={tenant.tenant_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{tenant.tenant_name}</p>
                          <p className="text-xs text-gray-500">{tenant.tenant_slug}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm">{tenant.total_users}</td>
                      <td className="px-4 py-3 text-center text-sm">{tenant.total_cases}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                          tenant.active_cases > 0 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {tenant.active_cases}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm">{tenant.total_staff}</td>
                      <td className="px-4 py-3 text-center text-sm">{tenant.total_workflows}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {tenant.last_case_created 
                          ? new Date(tenant.last_case_created).toLocaleDateString()
                          : 'No cases'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Security Notice */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 text-center">
        <p className="text-sm text-gray-600">
          <SafeIcon icon={FiShield} className="inline w-4 h-4 mr-1" />
          All actions are logged for security and compliance purposes.
        </p>
      </div>
    </div>
  );
};

export default PlatformDashboard;
